import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';

interface DraggableDialogProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    initialPosition?: { x: number; y: number };
    width?: string;
    height?: string;
}

export const DraggableDialog: React.FC<DraggableDialogProps> = ({
    isOpen,
    onClose,
    title = "Dialog",
    children,
    initialPosition = { x: window.innerWidth - 520, y: 100 },
    width = "30rem",
    height = "calc(100vh - 200px)"
}) => {
    const [position, setPosition] = useState(initialPosition);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const dialogRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;

        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging) return;

            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;

            // Keep dialog within viewport bounds
            const maxX = window.innerWidth - (dialogRef.current?.offsetWidth || 0);
            const maxY = window.innerHeight - (dialogRef.current?.offsetHeight || 0);

            setPosition({
                x: Math.max(0, Math.min(newX, maxX)),
                y: Math.max(0, Math.min(newY, maxY))
            });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset, isOpen]);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (dialogRef.current) {
            const rect = dialogRef.current.getBoundingClientRect();
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
            setIsDragging(true);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop overlay */}
            <div
                className="fixed inset-0 bg-black/20 z-40"
                onClick={onClose}
            />

            {/* Draggable dialog */}
            <div
                ref={dialogRef}
                className="fixed bg-white rounded-lg shadow-2xl border border-gray-300 z-50 flex flex-col"
                style={{
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                    width: width,
                    maxHeight: height,
                    cursor: isDragging ? 'grabbing' : 'default'
                }}
            >
                {/* Header - draggable area */}
                <div
                    className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-gray-100 to-gray-200 border-b border-gray-300 rounded-t-lg cursor-grab active:cursor-grabbing"
                    onMouseDown={handleMouseDown}
                >
                    <h3 className="font-semibold text-gray-800 select-none">{title}</h3>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="h-8 w-8 p-0 hover:bg-gray-300 rounded-full"
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto p-4">
                    {children}
                </div>
            </div>
        </>
    );
};
